import React from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { STATUS_TOOLTIPS, type PositionDetail } from '@/types/trades.types';
// Direct import (not the @/components/ui barrel) to avoid pulling the inventory
// Table/cross-dnd chain into the trade-detail tree.
import { StatusPill } from '@/components/ui/StatusPill';
import { getEffectiveTradeStatus } from '@/utils/trades/effective-status';
import { collateralSearchForTrade } from '@/features/collateral/collateral-search';

interface TradeHeaderProps {
  trade: PositionDetail;
  onRefresh: () => void;
  actionSlot?: React.ReactNode;
}

// Two <Link>s, because the back control now points at either route and TanStack
// types `to` + `search` as one unit — the class list is shared so they cannot
// drift visually.
const BACK_LINK_CLASSES =
  'inline-flex flex-none items-center gap-1.5 rounded-lg border border-lr-border bg-lr-surface px-2.5 py-1.5 text-sm font-medium text-lr-fg-2 transition-colors hover:bg-lr-bg hover:text-lr-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent focus-visible:ring-offset-2';

const TYPE_LABEL: Record<string, string> = {
  lend: 'Stock Lending',
  borrow: 'Stock Borrowing',
  repo: 'Repo',
  reverse_repo: 'Reverse Repo',
};

/**
 * Trade identity header (Phase-15 redesign, Step 3; single-row recompose LR-478).
 * ONE full-width row: an origin-aware back control + inline identity (TRD-id +
 * status pill, then direction chip, quantity, ticker · counterparty · agreement)
 * on the left, the action surface + Refresh on the right. Risk figures live in
 * the KPI strip; this header is wayfinding + identity + actions only. Replaces
 * the former two-row layout (separate breadcrumb band over a large title row).
 */
export function TradeHeader({ trade, onRefresh, actionSlot }: TradeHeaderProps) {
  const directionLabel = TYPE_LABEL[trade.trade_type] ?? trade.trade_type_display ?? trade.trade_type;
  // Prefer the bare framework code (GMSLA / GMRA): the backend's agreement_display
  // hardcodes a "- Lending" suffix even on borrow / reverse-repo, which misreads as
  // direction. Direction already lives in the trade_type chip below (F6).
  const agreementLabel = trade.agreement ? trade.agreement.toUpperCase() : (trade.agreement_display ?? '');

  // LR-478: origin-aware back link. `from` (+ `cp` for MTM) arrive on the search
  // of the trade-ID Link that opened this page; on a direct link / refresh `from`
  // is undefined → default to the Trades blotter. The back-link label and its
  // return target both reflect the origin ("Back to Mark to Market" returns to
  // the scoped MTM view, etc.).
  const { from, cp } = useSearch({ from: '/trades/$tradeId' });
  const rootLabel =
    from === 'mtm' ? 'Collateral Manager' : from === 'history' ? 'History' : 'Trades';
  // Return search for the back link (shape matches the /trades route's
  // validateSearch). MTM keeps the cp scope — an undefined/stale cp degrades to
  // unscoped MTM (the honest fallback) rather than a broken scoped link.
  const rootSearch =
    from === 'history'
      ? { mode: 'history' as const, cp: undefined }
      : { mode: undefined, cp: undefined };
  // LR-587: the marking workspace is a Collateral Manager subtab now, and WHICH
  // subtab is a property of the trade — the same rule the row action and "View in
  // MTM" use. `from: 'mtm'` alone would send every origin to GMSLA / Cash Pool
  // Fee, so a repo trade opened from the GMRA margin-call view came back to a
  // grid its own trade is not in.
  const collateralSearch = collateralSearchForTrade(trade, cp);

  return (
    // LR-478: sticks to the viewport top on scroll (the app shell is min-h-screen,
    // so the WINDOW is the scroll container — a plain position:sticky pins here).
    <div className="sticky top-0 z-20 border-b border-lr-border bg-lr-surface py-3">
      {/* LR-478 single row: [back + identity] ........spacer........ [actions + refresh].
          Full-bleed across the working body (NOT the centred max-w-[1440px] the KPI
          strip/content use) so the back control sits hard-left and the actions
          hard-right — using the whole width instead of a cramped centre band.
          flex-wrap lets the action cluster drop to its own right-aligned line on a
          narrow viewport; the identity (min-w-0) yields horizontal space first. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6">
        <div className="flex min-w-0 items-center gap-3">
          {/* Origin-aware back control — a plain <Link> (role=link, deliberately
              NOT role=tab, so it never collides with the blotter's TradesModeTabs
              in a11y queries). Carries the SAME origin return target the former
              breadcrumb root used; aria-label names the destination. */}
          {from === 'mtm' ? (
            <Link
              to="/collateral"
              search={collateralSearch}
              aria-label={`Back to ${rootLabel}`}
              className={BACK_LINK_CLASSES}
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              <span className="whitespace-nowrap">{rootLabel}</span>
            </Link>
          ) : (
            <Link
              to="/trades"
              search={rootSearch}
              aria-label={`Back to ${rootLabel}`}
              className={BACK_LINK_CLASSES}
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              <span className="whitespace-nowrap">{rootLabel}</span>
            </Link>
          )}

          <span className="h-6 w-px flex-none bg-lr-border" aria-hidden="true" />

          {/* Inline identity: TRD-id + status, then direction · qty · ticker ·
              counterparty · agreement. Wraps within its own min-w-0 box. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="flex items-center gap-2 text-[18px] font-bold leading-tight tracking-[-0.2px] text-lr-fg">
              TRD-{trade.id}
              {/* LR-497: derived effective status drives both the label
                  (via StatusPill → TRADE_STATUS_LABELS) and the colour, so the
                  header matches the trades-list badge — including the FOP
                  "Partially Settled" / recall "Pending Partial Settlement"
                  buckets the backend status_display can't express. */}
              {/* LR-544: hover tooltip explaining the lifecycle state (esp. the
                  "Pending Trade Date" wait). The banner below is the primary
                  explainer; this is the at-a-glance hint on the status badge. */}
              <StatusPill
                status={getEffectiveTradeStatus(trade)}
                dot={trade.status === 'active'}
                title={STATUS_TOOLTIPS[getEffectiveTradeStatus(trade)]}
              />
            </h1>
            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-lr-muted">
              <StatusPill tone="accent">{directionLabel}</StatusPill>
              <span className="lr-num font-semibold text-lr-fg-2">{trade.quantity}</span>
              <span className="font-semibold text-lr-fg-2">{trade.symbol_id}</span>
              <span className="text-lr-border-2">·</span>
              <span className="font-semibold text-lr-fg-2">{trade.counterparty_name}</span>
              {agreementLabel && (
                <>
                  <span className="text-lr-border-2">·</span>
                  <span>{agreementLabel}</span>
                </>
              )}
            </span>
          </div>
        </div>

        <div className="ml-auto flex flex-none items-center gap-2">
          {actionSlot}
          <button
            onClick={onRefresh}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
